import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  Crown,
  Sparkles,
  CheckCircle,
  ArrowUpCircle,
  Zap,
  Star,
  Lock,
  TrendingUp,
  Loader2
} from 'lucide-react';
import { subscriptionService, SubscriptionPlan } from '../../services/subscription';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
  reason: 'credits_exceeded' | 'syncs_exceeded' | 'stores_exceeded' | 'trial_expired' | 'no_subscription';
  currentPlan?: string;
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  feature,
  reason,
  currentPlan
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch plans when modal opens
  useEffect(() => {
    if (isOpen && plans.length === 0) {
      setIsLoading(true);
      subscriptionService.getPlans()
        .then((fetchedPlans) => {
          // Filter to show popular upgrade options (Bronze, Silver, Gold)
          const relevantPlans = fetchedPlans.filter(
            (p) => p.name === 'Bronze' || p.name === 'Silver' || p.name === 'Gold'
          );
          setPlans(relevantPlans);
        })
        .catch((error) => {
          console.error('Failed to fetch plans:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, plans.length]);

  // Format price for display
  const formatPrice = (price: number) => {
    return `$${price}`;
  };

  // Get features list for a plan
  const getPlanFeatures = (plan: SubscriptionPlan) => {
    const formatLimit = (limit: number, singular: string, plural: string) => {
      if (limit >= 999999) return `Unlimited ${plural}`;
      return `${limit} ${limit > 1 ? plural : singular}`;
    };

    const features = [
      `${(plan.usage_limit / 1000).toFixed(0)}k Credits`,
      formatLimit(plan.store_limit, 'Connector', 'Connectors'),
      formatLimit(plan.sync_limit, 'Monthly Sync', 'Monthly Syncs'),
    ];

    // Add feature-specific items based on plan
    if (plan.name === 'Gold') {
      features.push('AI Processing');
    } else if (plan.name === 'Enterprise') {
      features.push('Priority Support');
    }

    return features;
  };

  const getConfig = () => {
    switch (reason) {
      case 'credits_exceeded':
        return {
          icon: <Zap className="h-12 w-12 text-orange-500" />,
          title: 'Credits Limit Reached',
          description: `You've used all your monthly credits. Upgrade your plan to continue using ${feature}.`,
          highlight: 'Get more credits with a higher plan',
          recommendedPlan: 'Silver',
          ctaText: 'Upgrade Now',
          urgency: 'high'
        };

      case 'syncs_exceeded':
        return {
          icon: <TrendingUp className="h-12 w-12 text-blue-500" />,
          title: 'Monthly Sync Limit Reached',
          description: `You've used all your monthly syncs. Upgrade to continue syncing your product catalog.`,
          highlight: 'Unlimited syncs available on all plans',
          recommendedPlan: 'Bronze',
          ctaText: 'Upgrade Now',
          urgency: 'high'
        };

      case 'stores_exceeded':
        return {
          icon: <Crown className="h-12 w-12 text-purple-500" />,
          title: 'Connector Limit Reached',
          description: `Your current plan has reached its connector limit. Upgrade to add more connectors.`,
          highlight: 'Get unlimited connectors with Bronze plan or higher',
          recommendedPlan: 'Bronze',
          ctaText: 'Upgrade Now',
          urgency: 'medium'
        };

      case 'trial_expired':
        return {
          icon: <Star className="h-12 w-12 text-red-500" />,
          title: 'Trial Expired',
          description: 'Your free trial has ended. Choose a plan to continue transforming your product images with AI.',
          highlight: 'Start with Starter plan for just $19/month',
          recommendedPlan: 'Starter',
          ctaText: 'Choose Plan',
          urgency: 'high'
        };

      case 'no_subscription':
        return {
          icon: <Lock className="h-12 w-12 text-gray-500" />,
          title: 'Subscription Required',
          description: `Access to ${feature} requires an active subscription. Choose a plan to unlock all features.`,
          highlight: 'Plans starting at just $19/month',
          recommendedPlan: 'Starter',
          ctaText: 'View Plans',
          urgency: 'medium'
        };

      default:
        return {
          icon: <Sparkles className="h-12 w-12 text-blue-500" />,
          title: 'Upgrade Required',
          description: 'This feature requires a subscription.',
          highlight: 'Unlock premium features',
          recommendedPlan: 'Bronze',
          ctaText: 'Upgrade',
          urgency: 'medium'
        };
    }
  };

  const config = getConfig();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader className="text-center space-y-4">
          <div className="mx-auto p-4 rounded-full bg-muted">
            {config.icon}
          </div>
          <DialogTitle className="text-2xl font-bold">{config.title}</DialogTitle>
          <DialogDescription className="text-base text-muted-foreground max-w-md mx-auto">
            {config.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-6">
          {/* Highlight Banner */}
          <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 font-semibold text-primary">
              <Crown className="h-5 w-5" />
              {config.highlight}
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isLoading ? (
              <div className="col-span-2 flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : plans.length > 0 ? (
              plans.map((plan) => {
                const isPopular = plan.name === 'Gold';
                const features = getPlanFeatures(plan);

                return (
                  <Card
                    key={plan.name}
                    className={`relative transition-all duration-200 hover:shadow-lg ${
                      plan.name === config.recommendedPlan
                        ? 'border-2 border-primary shadow-xl bg-gradient-to-br from-primary/5 to-accent/5'
                        : 'border border-border hover:border-border-hover'
                    }`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-gradient-primary text-white">
                          <Crown className="h-3 w-3 mr-1" />
                          Most Popular
                        </Badge>
                      </div>
                    )}

                    <CardContent className="p-6 text-center space-y-4">
                      <div>
                        <h3 className="text-xl font-bold">{plan.name}</h3>
                        <div className="text-2xl font-bold text-primary mt-1">
                          {formatPrice(plan.monthly_price)}
                          <span className="text-sm font-normal text-muted-foreground">/month</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        {features.map((feature) => (
                          <div key={feature} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        asChild
                        className={`w-full gap-2 ${
                          plan.name === config.recommendedPlan
                            ? 'bg-gradient-primary hover:shadow-lg'
                            : ''
                        }`}
                        variant={plan.name === config.recommendedPlan ? "default" : "outline"}
                      >
                        <Link to={`/billing?tab=plans&highlight=${plan.name.toLowerCase()}`}>
                          <ArrowUpCircle className="h-4 w-4" />
                          Choose {plan.name}
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <div className="col-span-2 text-center py-8 text-muted-foreground">
                Unable to load plans. Please try again.
              </div>
            )}
          </div>

          {/* Bottom Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <Button variant="ghost" onClick={onClose}>
              Maybe Later
            </Button>
            
            <div className="flex items-center gap-3">
              <div className="text-xs text-muted-foreground">
                ✨ 14-day money-back guarantee
              </div>
              <Button asChild size="lg" className="gap-2 bg-gradient-primary">
                <Link to="/billing?tab=plans">
                  <Sparkles className="h-4 w-4" />
                  {config.ctaText}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};